import React, { useState, useEffect } from 'react';
import { Loader2, Trash2, Mail, MailOpen, Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import { adminContactAPI } from '../../services/adminApi';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = async () => {
    try {
      const data = await adminContactAPI.getAll();
      setMessages(data);
    } catch (err) {
      toast.error('Mesajlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      await adminContactAPI.markAsRead(id);
      fetchMessages();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu mesaj silinecek. Emin misiniz?')) return;
    
    try {
      await adminContactAPI.delete(id);
      toast.success('Mesaj silindi');
      setSelectedMessage(null);
      fetchMessages();
    } catch (err) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    if (!message.is_read) {
      await handleMarkAsRead(message.id);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-messages">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>İletişim Mesajları</h1>
        <span className="text-sm px-3 py-1 rounded-full" style={{ 
          background: 'rgba(245, 158, 11, 0.2)', 
          color: '#F59E0B' 
        }}>
          {messages.filter(m => !m.is_read).length} okunmamış
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-1 glass-card p-0 overflow-hidden max-h-[600px] overflow-y-auto">
          {messages.length === 0 ? (
            <div className="p-8 text-center" style={{ color: '#94A3B8' }}>
              Henüz mesaj yok
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                onClick={() => openMessage(message)}
                className={`p-4 cursor-pointer transition-all border-b ${
                  selectedMessage?.id === message.id ? 'bg-opacity-100' : 'bg-opacity-0'
                }`}
                style={{
                  background: selectedMessage?.id === message.id 
                    ? 'rgba(0, 240, 255, 0.1)' 
                    : message.is_read ? 'transparent' : 'rgba(245, 158, 11, 0.05)',
                  borderColor: 'rgba(0, 240, 255, 0.1)'
                }}
              >
                <div className="flex items-start gap-3">
                  {message.is_read ? (
                    <MailOpen size={18} style={{ color: '#64748B' }} />
                  ) : (
                    <Mail size={18} style={{ color: '#F59E0B' }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" style={{ 
                      color: message.is_read ? '#94A3B8' : '#F8FAFC' 
                    }}>
                      {message.name}
                    </p>
                    <p className="text-sm truncate" style={{ color: '#64748B' }}>
                      {message.subject}
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#475569' }}>
                      {new Date(message.created_at).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2 glass-card p-6">
          {selectedMessage ? (
            <div>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold" style={{ color: '#F8FAFC' }}>
                    {selectedMessage.subject}
                  </h2>
                  <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                    {selectedMessage.name} &lt;{selectedMessage.email}&gt;
                  </p>
                  <p className="text-xs mt-1" style={{ color: '#64748B' }}>
                    {new Date(selectedMessage.created_at).toLocaleString('tr-TR')}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(selectedMessage.id)}
                  className="p-2 rounded-lg transition-colors"
                  style={{ color: '#EF4444', background: 'rgba(239, 68, 68, 0.1)' }}
                >
                  <Trash2 size={18} />
                </button>
              </div>

              <div 
                className="p-4 rounded-lg whitespace-pre-wrap"
                style={{ 
                  background: 'rgba(15, 23, 42, 0.6)',
                  color: '#F8FAFC',
                  minHeight: '200px'
                }}
              >
                {selectedMessage.message}
              </div>

              <div className="flex items-center gap-4 mt-6">
                <a
                  href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium"
                  style={{
                    background: '#00F0FF',
                    color: '#0F172A'
                  }}
                >
                  <Mail size={16} />
                  Yanıtla
                </a>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg"
                  style={{ color: '#94A3B8' }}
                >
                  <X size={16} />
                  Kapat
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64" style={{ color: '#64748B' }}>
              <Eye size={48} className="mb-4 opacity-50" />
              <p>Görüntülemek için bir mesaj seçin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;
